<template>
  <v-card>
    <v-list slim density="compact">
      <v-list-subheader class="mb-3 pl-2 mx-2 d-flex align-center justify-space-between">
        <span>
          {{ $t('customForms.headers.addOptionsToYourDropDownListMaxOptions', { maxChoices }) }}
        </span>
        <icon-tooltip-button
          :tooltip="
            isBulkInput
              ? $t('customForms.tooltips.showSingleOptionInput')
              : $t('customForms.tooltips.showBulkOptionInput')
          "
          color="secondary"
          @click="toggleIsBulkInput"
          :icon="isBulkInput ? 'mdi-text-short' : 'mdi-text-long'"></icon-tooltip-button>
      </v-list-subheader>
      <template v-if="isBulkInput">
        <v-container class="d-flex flex-column align-center is-relative pt-0">
          <div class="overlay-base pa-3 d-flex flex-column justify-center" v-if="isProcessing">
            <h3 class="z-100 text-center mb-3">
              {{ $t('customForms.messages.processingItems') }}...
            </h3>
            <v-progress-linear indeterminate class="z-100 is-relative"></v-progress-linear>
            <div class="overlay-obscure overlay-base"></div>
          </div>
          <v-textarea
            v-model="currentBulkValueTemplate"
            persistent-placeholder
            class="full-width text-area mt-0"
            :placeholder="
              $t(
                'customForms.placeholders.copyPasteIntoThisBoxOptionsMustBeCommaOrNewLineSeparated'
              )
            "></v-textarea>
          <v-btn
            class="ml-3"
            @click="addCurrentBulkValueTemplate"
            :disabled="disableAddChoiceButton">
            {{ $t('global.basicTerms.add') }}
          </v-btn>
        </v-container>
      </template>
      <v-container v-else class="d-flex">
        <v-text-field
          density="compact"
          class="mx-2"
          v-model="currentValueTemplate"
          type="text"
          :label="$t('customForms.labels.newOption')"
          data-testid="custom-field-drop-down-choices-form-option-input"
          :placeholder="`${$t('customForms.placeholders.enterATextAndAddItToTheList')}...`"
          @keydown.enter.stop
          @keypress.enter="addCurrentValueTemplate"></v-text-field>
        <v-btn
          data-testid="custom-field-drop-down-choices-add-button"
          class="ml-3"
          @click="addCurrentValueTemplate"
          :disabled="disableAddChoiceButton">
          {{ $t('global.basicTerms.add') }}
        </v-btn>
      </v-container>
      <br />
      <v-list-item v-for="(item, i) in dropDownValuesLocal" :key="i" :title="null">
        {{ item }}
        <template #prepend>
          <v-icon>mdi-circle-small</v-icon>
        </template>

        <template #append>
          <v-btn
            variant="text"
            size="x-small"
            icon
            :title="$t('customForms.tooltips.removeItem')"
            @click="removeValueTemplate(item)">
            <v-icon color="red">mdi-delete-outline</v-icon>
          </v-btn>
        </template>
      </v-list-item>
      <v-list-group v-if="isEmptyListOfChoices">
        <p class="text-grey text-center">{{ $t('customForms.messages.emptyList') }}!</p>
      </v-list-group>
    </v-list>
    <p class="text-grey text-right small mr-3">
      <small>{{ $t('customForms.messages.listItemsAreAlphabeticallySorted') }}</small>
    </p>
  </v-card>
</template>

<script>
export default {
  name: 'CustomFieldDropDownChoicesForm',
  data() {
    return {
      currentValueTemplate: '',
      currentBulkValueTemplate: '',
      dropDownValuesLocal: [],
      isBulkInput: false,
      isProcessing: false
    };
  },
  props: {
    modelValue: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    showDropDownValuesList() {
      return this.type === this.novaCore.CustomFieldType.DropDown;
    },
    disableAddChoiceButton() {
      return (
        ((!this.currentValueTemplate && !this.currentBulkValueTemplate) ||
          this.dropDownValuesLocal.length >= this.novaCore.CUSTOM_FIELD_DROP_DOWN_OPTIONS_LIMIT) &&
        !this.isProcessing
      );
    },
    isEmptyListOfChoices() {
      return this.dropDownValuesLocal.length === 0;
    },
    maxChoices() {
      return this.novaCore.CUSTOM_FIELD_DROP_DOWN_OPTIONS_LIMIT;
    }
  },
  methods: {
    addCurrentValueTemplate() {
      if (this.dropDownValuesLocal.length <= this.maxChoices) {
        const isNewOption = !this.dropDownValuesLocal.includes(this.currentValueTemplate);
        if (isNewOption) {
          this.dropDownValuesLocal.push(this.currentValueTemplate);
          this.dropDownValuesLocal.sort();
          this.currentValueTemplate = '';
        } else {
          this.notify(
            this.$t('customForms.messages.thisOptionIsAlreadyOnTheListOfChoices'),
            'warning'
          );
        }
      } else {
        this.notify(
          this.$t('customForms.messages.aMaximumOfOptionsAreAllowed', {
            maxChoices: this.maxChoices
          }),
          'warning'
        );
      }
    },
    async addCurrentBulkValueTemplate() {
      this.isProcessing = true;
      const itemsToPush = [];
      const bulkValueTemplateCopy = this.novaCore.deepClone(this.currentBulkValueTemplate);
      const itemArray = bulkValueTemplateCopy.replace(/,\s+/g, ',').split(/[\n,]/);
      let duplicateItems = false;
      let maxChoices = false;
      itemArray.forEach(item => {
        if (this.dropDownValuesLocal.length <= this.maxChoices) {
          const trimmedItem = item.trim();
          const isNewOption =
            !itemsToPush.includes(trimmedItem) && !this.dropDownValuesLocal.includes(trimmedItem);
          if (isNewOption) {
            // Ignore empty entries
            if (trimmedItem.length > 0) {
              itemsToPush.push(trimmedItem);
            }
          }
        } else {
          maxChoices = true;
          this.notify(
            this.$t('customForms.messages.aMaximumOfOptionsAreAllowed', {
              maxChoices: this.maxChoices
            }),
            'warning'
          );
        }
      });
      if (!duplicateItems && !maxChoices) {
        this.currentBulkValueTemplate = '';
      } else {
        return;
      }
      this.isProcessing = false;
      if (itemsToPush.length > 0) {
        this.dropDownValuesLocal = [...this.dropDownValuesLocal, ...itemsToPush];
        this.dropDownValuesLocal.sort();
        this.toggleIsBulkInput();
      }
    },
    removeValueTemplate(item) {
      this.dropDownValuesLocal = this.dropDownValuesLocal.filter(i => i !== item);
      this.notify(
        this.$t('customForms.messages.optionHasBeenRemovedFromTheListOfChoices', { item }),
        'info'
      );
    },
    toggleIsBulkInput() {
      if (this.isProcessing) {
        return;
      }
      this.currentBulkValueTemplate = '';
      this.currentValueTemplate = '';
      this.isBulkInput = !this.isBulkInput;
    }
  },
  watch: {
    dropDownValuesLocal() {
      this.$emit('update:model-value', this.dropDownValuesLocal);
    }
  },
  mounted() {
    this.dropDownValuesLocal = [...this.modelValue];
  }
};
</script>
